import React, { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";

import "./DamagesVideoAnalysis.scss";
import { translationService } from "../../../services/translation/TranslationService";
import "./DamagesVideo.scss";
import useTheme from "../../../services/theme-service/ThemeService";
import LoadingSpinner from "../../../components/loading-spinner/LoadingSpinner";
import Svg from "../../../components/svg/Svg";
import { Warning, Info } from "../../../components/svg/svgImageList";
import {
  RunVideoAnalysis,
  RunVideoAnalysisFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import { globalModule } from "../../../services/service-provider/GlobalModule";
import { videoPlayerService } from "../../../services/video-player/VideoPlayerService";
import { PubSubEvents } from "../../../services/PubSubEvents";

import { Timestamp, Timestamps } from "../../../services/video-player/TimestampsData";
import Legend from "./video-analysis-diagram/tools/Legend";
import { VideoAnalysisDiagramProps } from "./video-analysis-diagram/VideoAnalysisDiagram";

const srcDoc = require("srcdoc-polyfill");
const initialIframeHeight = 350;

interface DamagesVideoAnalysisProps extends VideoAnalysisDiagramProps{
  inspectionLocked: boolean;
}

export default observer(function DamagesVideoAnalysis(props: DamagesVideoAnalysisProps) {
  const { videoAnalysis, damagesStorage, video, onVAFinished, inspectionLocked } =
    props;
  const { themeClass } = useTheme();
  const iframeRef = useRef(null);
  const { videoProgress } = videoPlayerService;

  const [iframeHeight, setIframeHeight] = useState(initialIframeHeight);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const videoAnalysisNotYetStarted = useMemo(() => {
    return (
      !videoAnalysis.isLoading &&
      videoAnalysis?.analysisNotYetStarted &&
      !videoAnalysis?.analysisInProgress
    );
  }, [
    videoAnalysis?.analysisNotYetStarted,
    videoAnalysis?.analysisInProgress,
    videoAnalysis.isLoading,
  ]);

  const handleRetryAnalysis = useCallback(async () => {
    snackbarNotificationsService.showSuccess(
      "damages.DamagesVideoAnalysis.video-analysis-started-snackbar"
    );
    videoAnalysis?.startVideoAnalysis();
  }, [videoAnalysis]);

  const updateIframeHeight = useCallback((subplotsVisible: number) => {
    const subplotHeight = 32;
    const diagramExtraElementsHeight = 130;
    if (subplotsVisible > 5)
      setIframeHeight(subplotsVisible * subplotHeight + diagramExtraElementsHeight);
    else setIframeHeight(initialIframeHeight);
  }, []);

  useEffect(() => {
    var analysisSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoAnalysisFinished,
      async (_event: string, notification: Notification) => {
          setIframeLoaded(false);
          onVAFinished(notification.data.fileId);
          setIframeLoaded(true);
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(analysisSubscribeToken);
    };
  }, [onVAFinished]);

  useEffect(() => {
    if (
      videoAnalysis?.analysisContent &&
      !videoAnalysis.analysisInProgress &&
      videoAnalysis?.analysisIsAvailable
    ) {
      srcDoc.set(iframeRef.current, videoAnalysis?.analysisContent);
      (iframeRef.current as any).onload = () => {
        setIframeLoaded(true);
      };
    }
  }, [videoAnalysis, videoAnalysis?.analysisContent, videoAnalysis.analysisInProgress, videoAnalysis?.analysisIsAvailable]);

  useEffect(() => {
    if (translationService.language !== videoAnalysis.language) {
      const reloadAnalysis = async () => {
        await videoAnalysis.reload();
        videoAnalysis.setLanguage(translationService.language);
      };

      reloadAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoAnalysis, translationService.language]);

  useEffect(() => {
    globalModule.videoService = {
      ...globalModule.videoService,
      updateIframeHeight,
    };
  }, [updateIframeHeight]);

  useEffect(() => {
    if (iframeLoaded) {
      const videoAnalysisDiagram: any = window.document.getElementById(
        "damages-video-analysis-iframe"
      );
      if (
        videoAnalysisDiagram &&
        videoAnalysisDiagram.contentWindow.update_current_time_indicator &&
        videoProgress > 0
      ) {
        videoAnalysisDiagram.contentWindow.update_current_time_indicator(videoProgress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeLoaded]);

  useEffect(() => {
    if (!iframeLoaded) return;

    const damageCodesTimestampsObject: { [key: string]: number[] } = {};

    (damagesStorage.videoPlayerTimestamps as Timestamps).items.forEach((element: Timestamp) => {
      if (damageCodesTimestampsObject[element.mainCode])
        damageCodesTimestampsObject[element.mainCode].push(element.timestamp);
      else damageCodesTimestampsObject[element.mainCode] = [element.timestamp];
    });

    videoAnalysis.loadMainCodesDetected();

    const supportedCodes = videoAnalysis.detectedMainCodes.map((mainCode) => mainCode.code);

    Object.entries(damageCodesTimestampsObject).forEach(([code, timestamps]) => {
      if (supportedCodes.includes(code))
        videoAnalysis.updateDamageSeriesIntoGraph(code, timestamps);
      else {
        timestamps.forEach((timestamp) => {
          videoAnalysis.addDamageToGraph(code, timestamp);
        });
      }
    });
  }, [damagesStorage, iframeLoaded, videoAnalysis]);

  return (
    <>
      <div
        className={themeClass(
          ["video-analysis"],
          [videoAnalysisNotYetStarted ? "not-started" : ""]
        )}
      >
        {videoAnalysis.isLoading && <LoadingSpinner />}
        {video?.isConverting &&
          videoAnalysis?.analysisInProgress && (
            <div className={themeClass(["video-not-available"])}>
              <LoadingSpinner spinnerOnly />
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message"
                )}
              </p>
              <p className={themeClass(["message"])}>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-format-message-automatic-start"
                )}
              </p>
            </div>
          )}
        {video?.conversionFailed &&
          videoAnalysis?.analysisInProgress && (
            <>
              <div className={themeClass(["video-failed-conversion"])}>
                <div className="svg-warning">
                  <Svg svg={Warning} width={100} />
                </div>
                {permissionsService.userHasOneOfThePermissions([
                  RunVideoAnalysis,
                  RunVideoAnalysisFromAssignedProject,
                ]) ? (
                  <>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-admin-user-message"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {translationService.translate(
                        "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-retry"
                      )}
                    </p>
                    <p className={themeClass(["message"])}>
                      {` ${translationService.translate(
                        "damages.DamagesVideo.video-conversion-error-warning-contact"
                      )} `}
                    </p>
                  </>
                ) : (
                  <p className={themeClass(["message"])}>
                    {translationService.translate(
                      "damages.DamagesVideoAnalysis.video-analysis-failed-conversion-guest-message"
                    )}
                  </p>
                )}
              </div>
            </>
          )}
        {videoAnalysisNotYetStarted && (
          <div className="message-info">
            <Svg className="svg-info" svg={Info} />
            <span>
              {translationService.translate(
                "damages.DamagesVideoAnalysis.video-analysis-not-started"
              )}
            </span>
          </div>
        )}
        {videoAnalysis?.analysisInProgress &&
          video?.isAvailable && (
            <>
              <LoadingSpinner />
              <p>
                {translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-in-progress"
                )}
              </p>
            </>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisInProgress &&
          videoAnalysis?.analysisIsAvailable && (
            <div className="va-diagram-container">
              <iframe
                ref={iframeRef}
                id="damages-video-analysis-iframe"
                className="video-analysis-iframe"
                title="Video analysis"
                style={{ height: `${iframeHeight}px` }}
              />
              {iframeLoaded && (
                <Legend
                  videoAnalysisService={videoAnalysis}
                  currentInspectionIsLocked={
                    inspectionLocked
                  }
                />
              )}
            </div>
          )}
        {!videoAnalysis.isLoading &&
          !videoAnalysis.analysisInProgress &&
          videoAnalysis?.analysisFailed && (
            <div className={themeClass(["video-failed-analysis"])}>
              <div className="svg-warning">
                <Svg svg={Warning} width={100} />
              </div>
              <br />
              <p className={themeClass(["message"])}>
                {`${translationService.translate(
                  "damages.DamagesVideoAnalysis.video-analysis-failed"
                )} `}
                {permissionsService.userHasOneOfThePermissions([
                  RunVideoAnalysis,
                  RunVideoAnalysisFromAssignedProject,
                ]) && (
                  <>
                    <span onClick={handleRetryAnalysis}>
                      {translationService.translate(
                        "damages.DamagesVideo.video-conversion-error-warning-link"
                      )}
                    </span>
                    <p className={themeClass(["message"])}>
                      {`${translationService.translate(
                        "damages.DamagesVideo.video-analysis-error-warning-contact"
                      )} `}
                    </p>
                  </>
                )}
                {!permissionsService.userHasOneOfThePermissions([
                  RunVideoAnalysis,
                  RunVideoAnalysisFromAssignedProject,
                ]) &&
                  translationService.translate(
                    "damages.DamagesVideoAnalysis.video-analysis-failed-guest-user-message"
                  )}
              </p>
              <br />
              <p className={themeClass(["message", "margin"])}> {videoAnalysis.errorMessage}</p>
            </div>
          )}
      </div>
    </>
  );
});
